import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AUTHENTICATION_TYPE, ROUTES } from 'shared/constants';
import userService from 'shared/services/user.service';
import { useQuery } from 'src/utils/router.hooks';
import { AuthPage } from '../auth-page-wrapper';
import { Loader } from 'src/components/core';
import { consumePostLoginRedirect } from 'src/utils/post-login-redirect';
import { consumePendingSsoUnlock } from 'src/utils/pending-sso-unlock';
import { message } from 'antd';

// Module-scoped rather than a `useRef`, so an authCode stays consumed even across a remount.
const consumedAuthCodes = new Set();

export function OauthCallback() {
  const isAuthenticated = userService.isAuthenticated;
  const hasPersonalSession = userService.hasPersonalSession;
  const query = useQuery();
  const navigator = useNavigate();
  const { provider } = useParams();

  const isSaml = provider === AUTHENTICATION_TYPE.SAML.toLowerCase();

  // this use effect is to handle SSO login flow
  useEffect(() => {
    const completeSSOLogin = async () => {
      if (!query.authCode || !query.userId || !isSaml || consumedAuthCodes.has(query.authCode)) {
        return;
      }
      consumedAuthCodes.add(query.authCode);

      // A round-trip started from a live session unlocks the SAML org on that session
      // instead of replacing it with a new login. `query.userId` is whom the identity
      // provider signed in; the server compares it with the session so a two-email user
      // gets a specific message instead of a generic failure. A magic-link guest session
      // falls through to a login, which replaces it.
      if (hasPersonalSession) {
        const { error } = await userService.activateSaml({
          authCode: query.authCode,
          assertedUserId: query.userId,
          // Which org was clicked, stashed before the redirect; see `pending-sso-unlock`.
          organizationId: consumePendingSsoUnlock(),
        });
        if (error) {
          message.error(error);
        }
        // Home, not a stashed redirect: that was captured for a *login* bounce and would
        // send the user somewhere unrelated after unlocking an org.
        navigator(ROUTES.HOME);
        return;
      }

      // A login never uses the stash, but drop anything left in it so it cannot leak into a
      // later unlock in this tab.
      consumePendingSsoUnlock();
      const result = await userService.verifyCode({ code: query.authCode, userId: query.userId });
      if (!result.error) {
        navigator(consumePostLoginRedirect() || ROUTES.ROOT);
      }
    };

    completeSSOLogin();
  }, [query.authCode, query.userId, isSaml, hasPersonalSession, navigator]);

  // this use effect is to handle oauth login flow
  useEffect(() => {
    // ignore on SSO login
    if (isSaml) {
      return;
    }

    // check if there is code in the query
    if (!query.code) {
      return navigator(ROUTES.WELCOME);
    }

    const redirectRoute = isAuthenticated
      ? consumePostLoginRedirect() || ROUTES.ROOT
      : ROUTES.WELCOME;

    // if there is code in the query, redirect to the welcome page
    return navigator(redirectRoute, {
      state: { query, provider },
    });
  }, [query, provider, navigator, isAuthenticated, isSaml]);

  return (
    <AuthPage title="Processing...">
      <Loader />
    </AuthPage>
  );
}
